import type { ReactNode } from 'react';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5f (builder C) — the legacy board's 13 stub tabs, ported 1:1 from
 * report/mvc/tabs/<value>/: Engineering mech_dwg / temperature_profile /
 * eng_hours; Manufacturing plant_hours / mfg_package / base_cut_list /
 * floor_cut_list / casing_wall_cut_list / roof_cut_list / belt_drive_select;
 * Purchasing parts_by_others_list; Shipping freight_request / bill_of_lading.
 * All 13 have live RequireJS aliases (report/mvc/app.js:128,178-208), so a
 * granted dispatch WOULD build them via ensureReportView (action.js:197-206).
 *
 * All 13 views are the SAME 39-41 line skeleton: NO Dispatcher listeners at
 * all (a generate dispatches nothing beyond the pane construction — no POST,
 * no fetch, no socket), initialize → render() SYNCHRONOUSLY (e.g.
 * mech_dwg.js:22-31), which compiles the tab template with {} or
 * {sectionName} and clears the .report-tabs-preloader — the filter_data
 * pattern → all 13 are IMMEDIATE_RENDER_TABS entries; `rendered` is ignored
 * and there is no generation-in-progress container for `faded` to touch.
 * None is reopenable (no open:generated_report listener → empty board on
 * re-open, the page default). The console.log('Hello <value>') calls
 * (:29-31 in each view) are debug noise, not ported.
 *
 * sectionName = the .current_report menu button's text, i.e. the report
 * display name (temperature_profile.js:27-30 et al.) → report.name here.
 * mech_dwg/plant_hours/belt_drive_select render template({})
 * (mech_dwg.js:27, plant_hours.js:27, belt_drive_select.js:27) and
 * eng_hours/mfg_package DO pass sectionName (eng_hours.js:27-29,
 * mfg_package.js:27-30) — but all five templates carry only literal text, so
 * each keeps its hardcoded "<Group>: <Title>" h3 line either way.
 *
 * dev3 note: NONE of the 13 values has a ksdm_master_ahu_report row (SQL
 * verified 5f), so they appear in neither board's menu — ported faithfully
 * anyway (the ahu_model / Management-family precedent: panes work when
 * granted).
 *
 * uvc_selection is NOT ported — dead by construction: report/mvc/app.js has
 * NO 'view.uvc_selection' alias (the tab aliases at app.js:55-224 skip it;
 * the only uvc modules are the accessory/component-selector ones at
 * app.js:258-262), so ensureReportView's fn.require('view.uvc_selection')
 * (action.js:200) can never resolve — the 41-line view
 * (tabs/uvc_selection/view/uvc_selection.js, which would render the
 * 1130-line static uvc_selection.tmpl) is unreachable from any dispatch.
 */

/** The literal-h3 shell shared by mech_dwg / eng_hours / plant_hours /
 *  mfg_package / belt_drive_select (mech_dwg.tmpl:1-9 = eng_hours.tmpl:1-9 =
 *  plant_hours.tmpl:1-9 = mfg_package.tmpl:1-9 = belt_drive_select.tmpl:1-9
 *  modulo the title text): a plain "<Group>: <Title>" text node (no
 *  group-name/report-name spans), the hardcoded 2017 time-stamp, and an
 *  EMPTY content-item. */
const LiteralStubTab = ({ report, title }: GeneratedTabPaneProps & { title: string }) => (
  // Wrapper = the static pane div (main.tmpl, class="col-xs-12") plus the
  // 'active' openReport adds at generate time (action.js:84).
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <div className="container-header clearfix">
      <h3 className="left">
        {title} <span className="time-stamp">Generated on: 2017.09.13 19:10:46</span>
      </h3>
    </div>
    <div className="content">
      <div className="content-item" />
    </div>
  </div>
);

/** mech_dwg.tmpl:1-9 (view mech_dwg.js:26-31). */
export const MechDwgTab = (props: GeneratedTabPaneProps) => (
  <LiteralStubTab {...props} title="Engineering: Mechanical Drawing" />
);

/** eng_hours.tmpl:1-9 (view eng_hours.js:26-32). */
export const EngHoursTab = (props: GeneratedTabPaneProps) => (
  <LiteralStubTab {...props} title="Engineering: Engineering Hours" />
);

/** plant_hours.tmpl:1-9 (view plant_hours.js:26-31). */
export const PlantHoursTab = (props: GeneratedTabPaneProps) => (
  <LiteralStubTab {...props} title="Manufacturing: Plant Hours" />
);

/** mfg_package.tmpl:1-9 (view mfg_package.js:26-33). */
export const MfgPackageTab = (props: GeneratedTabPaneProps) => (
  <LiteralStubTab {...props} title="Manufacturing: Mfg Package" />
);

/** belt_drive_select.tmpl:1-9 (view belt_drive_select.js:26-31). */
export const BeltDriveSelectTab = (props: GeneratedTabPaneProps) => (
  <LiteralStubTab {...props} title="Manufacturing: Belt Drive Selections" />
);

/** The group/report-name spans header shared by the other eight stubs
 *  (temperature_profile.tmpl:1-5 et al.): tooltip ": <name>", group-name and
 *  report-name ADJACENT, hardcoded 2017 time-stamp. `nameSpace` is the
 *  template-varying leading space inside .report-name (present in
 *  floor_cut_list/roof_cut_list/parts_by_others_list/freight_request/
 *  bill_of_lading; absent in temperature_profile/base_cut_list/
 *  casing_wall_cut_list). */
const StubTabHeader = ({
  group,
  name,
  nameSpace,
  colXs12,
}: {
  group: string;
  name: string;
  nameSpace?: boolean;
  /** bill_of_lading.tmpl:1 — its container-header carries col-xs-12. */
  colXs12?: boolean;
}) => (
  <div className={`container-header clearfix${colXs12 ? ' col-xs-12' : ''}`}>
    <h3 className="left">
      <span className="group-name" tooltip={`: ${name}`}>
        {group}
      </span>
      <span className="report-name">
        {nameSpace ? ' ' : ''}
        {name}
      </span>{' '}
      <span className="time-stamp">Generated on: 2017.09.13 19:10:46</span>
    </h3>
  </div>
);

/** freight_request.tmpl:1-9 (view freight_request.js:26-33) — spans header,
 *  EMPTY content-item. */
export const FreightRequestTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Shipping" name={report.name} nameSpace />
    <div className="content">
      <div className="content-item" />
    </div>
  </div>
);

/** bill_of_lading.tmpl:1-11 (view bill_of_lading.js:26-32) — col-xs-12
 *  container-header and the content nested in a report-content col-xs-12
 *  wrapper; EMPTY content-item. */
export const BillOfLadingTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Shipping" name={report.name} nameSpace colXs12 />
    <div className="report-content col-xs-12">
      <div className="content">
        <div className="content-item" />
      </div>
    </div>
  </div>
);

// ---- Temperature Profile ----

/** temperature_profile.tmpl:23-42 — the three placeholder rows. */
const TEMP_PROFILE_ROWS: Array<[string, string, string, string]> = [
  ['SP1', 'Plenum', '147.00 / 120.00', '110.00 / 64.25'],
  ['SF1', 'Filter', '58.55 / 58.00', '48.60 / 90.00'],
  ['SC1', 'Coil', '147.00 / 64.25', '48.60 / 90.00'],
];

/** temperature_profile.tmpl:1-46 (view temperature_profile.js:26-33) — a
 *  static placeholder table; content is `content col-xs-12` wrapping a bare
 *  col-xs-12 (NOT the report-content shape). The heading lis are ADJACENT
 *  (</li><li>). */
export const TemperatureProfileTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Engineering" name={report.name} />
    <div className="content col-xs-12">
      <div className="col-xs-12">
        <div className="content-item">
          <div className="rep-table-wrapper temp-profile-outputs-table">
            <ul className="table-heading">
              <li>
                <h3>Section Tag</h3>
              </li>
              <li>
                <h3>Component</h3>
              </li>
              <li>
                <h3>Temperature In DB / WB</h3> <span>(F)</span>
              </li>
              <li>
                <h3>Temperature Out DB / WB</h3> <span>(F)</span>
              </li>
            </ul>
            <ul className="table-rows">
              {TEMP_PROFILE_ROWS.map(([tag, component, tin, tout]) => (
                <li key={tag} className="row">
                  <h4>{tag}</h4> <h4>{component}</h4> <span>{tin}</span> <span>{tout}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ---- Base Cut List ----

/** base_cut_list.tmpl:22-104 — three placeholder rows, identical except the
 *  member name and tag prefix (BPM/PCM/SCM .1-.4); materials, UOMs and cuts
 *  repeat the same four values. */
const BASE_CUT_ROWS: Array<[string, string]> = [
  ['Perimeter Member', 'BPM'],
  ['Primary Cross Member', 'PCM'],
  ['Secondary Cross Member', 'SCM'],
];

const BASE_CUT_MATERIALS = [
  'Tube_Steel_G60_2x8',
  'Tube_Steel_G60_2x8',
  'Tube_Steel_G60_2x8',
  'Tube_Steel_G60_2x8',
];
const BASE_CUT_CUTS = ['182', '94.04', '182', '94.04'];

/** base_cut_list.tmpl:1-107 (view base_cut_list.js:26-32). */
export const BaseCutListTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Manufacturing" name={report.name} />
    <div className="content">
      <div className="content-item">
        <div className="rep-table-wrapper base-cut-outputs-table">
          <ul className="table-heading">
            <li>
              <h3>Part of</h3>
            </li>
            <li>
              <h3>Tag</h3>
            </li>
            <li>
              <h3>Material &amp; Size</h3>
            </li>
            <li>
              <h3>UOM</h3>
            </li>
            <li>
              <h3>Cut List</h3>
            </li>
          </ul>
          <ul className="table-rows">
            {BASE_CUT_ROWS.map(([member, prefix]) => (
              <li key={prefix} className="row">
                <h4>{member}</h4>{' '}
                <span className="outputs">
                  {[1, 2, 3, 4].map((n) => (
                    <span key={n}>{`${prefix}.${n}`}</span>
                  ))}
                </span>{' '}
                <span className="outputs">
                  {BASE_CUT_MATERIALS.map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
                </span>{' '}
                <span className="outputs">
                  {[1, 2, 3, 4].map((n) => (
                    <span key={n}>in</span>
                  ))}
                </span>{' '}
                <span className="outputs">
                  {BASE_CUT_CUTS.map((c, i) => (
                    <span key={i}>{c}</span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

// ---- Floor / Casing Wall / Roof Cut Lists ----

/** An outputs span holding N inner spans (the cut-list tmpls' repeated
 *  <span class="outputs"> groups). */
const OutputsSpan = ({ values }: { values: string[] }) => (
  <span className="outputs">
    {values.map((v, i) => (
      <span key={i}>{v}</span>
    ))}
  </span>
);

const CUT_LIST_MATERIALS = [
  'metal_sheet_g90_solid_14ga',
  'metal_sheet_g90_solid_14ga',
  'metal_sheet_g90_solid_14ga',
];

/** floor_cut_list.tmpl:1-72 (view floor_cut_list.js:26-33) — no Design
 *  column; heading "Cut List" carries the space-separated "d1 d2 … d7"
 *  span and the last column is "Panel Design". Rows: Floor Deck (the
 *  materials/uom/dims placeholders) then Floor Insulation / Floor
 *  Under-Liner with EMPTY single-span outputs; every row ends with a bare
 *  empty span. */
export const FloorCutListTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Manufacturing" name={report.name} nameSpace />
    <div className="content">
      <div className="content-item">
        <div className="rep-table-wrapper floor-cut-outputs-table">
          <ul className="table-heading">
            <li>
              <h3>Part of</h3>
            </li>
            <li>
              <h3>Material</h3>
            </li>
            <li>
              <h3>UOM</h3>
            </li>
            <li>
              <h3>Cut List</h3> <span>d1 d2 d3 d4 d5 d6 d7</span>
            </li>
            <li>
              <h3>Panel Design</h3>
            </li>
          </ul>
          <ul className="table-rows">
            <li className="row">
              <h4>Floor Deck</h4> <OutputsSpan values={CUT_LIST_MATERIALS} />{' '}
              <OutputsSpan values={['in', 'in', 'in']} />{' '}
              <OutputsSpan values={['1.5 x 2.0 x 6.0 x 2.0 x 1.5 x 72.0']} /> <span />
            </li>
            {['Floor Insulation', 'Floor Under-Liner'].map((part) => (
              <li key={part} className="row">
                <h4>{part}</h4> <OutputsSpan values={['']} /> <OutputsSpan values={['']} />{' '}
                <OutputsSpan values={['']} /> <span />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

/** The table shared VERBATIM by casing_wall_cut_list.tmpl:8-75 and
 *  roof_cut_list.tmpl:8-75 (only the wrapper class differs — the roof
 *  template repeats the Floor Deck/Insulation/Under-Liner rows [sic]):
 *  Design column (PD1/PD2/PD3), "Dimensions" heading with the x-separated
 *  "d1 x d2 … x d7" span, last column "Panel Style". */
const WallRoofCutTable = ({ tableClass }: { tableClass: string }) => (
  <div className={`rep-table-wrapper ${tableClass}`}>
    <ul className="table-heading">
      <li>
        <h3>Part of</h3>
      </li>
      <li>
        <h3>Design</h3>
      </li>
      <li>
        <h3>Material</h3>
      </li>
      <li>
        <h3>UOM</h3>
      </li>
      <li>
        <h3>Dimensions</h3> <span>d1 x d2 x d3 x d4 x d5 x d6 x d7</span>
      </li>
      <li>
        <h3>Panel Style</h3>
      </li>
    </ul>
    <ul className="table-rows">
      <li className="row">
        <h4>Floor Deck</h4> <span>PD1</span> <OutputsSpan values={CUT_LIST_MATERIALS} />{' '}
        <OutputsSpan values={['in', 'in', 'in']} />{' '}
        <OutputsSpan values={['1.5 x 2.0 x 6.0 x 2.0 x 1.5 x 72.0']} /> <span />
      </li>
      {(
        [
          ['Floor Insulation', 'PD2'],
          ['Floor Under-Liner', 'PD3'],
        ] as Array<[string, string]>
      ).map(([part, design]) => (
        <li key={design} className="row">
          <h4>{part}</h4> <span>{design}</span> <OutputsSpan values={['']} />{' '}
          <OutputsSpan values={['']} /> <OutputsSpan values={['']} /> <span />
        </li>
      ))}
    </ul>
  </div>
);

/** casing_wall_cut_list.tmpl:1-78 (view casing_wall_cut_list.js:26-32). */
export const CasingWallCutListTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Manufacturing" name={report.name} />
    <div className="content">
      <div className="content-item">
        <WallRoofCutTable tableClass="casing-wall-cut-outputs-table" />
      </div>
    </div>
  </div>
);

/** roof_cut_list.tmpl:1-78 (view roof_cut_list.js:26-33). */
export const RoofCutListTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Manufacturing" name={report.name} nameSpace />
    <div className="content">
      <div className="content-item">
        <WallRoofCutTable tableClass="roof-cut-outputs-table" />
      </div>
    </div>
  </div>
);

// ---- Parts By Others List ----

/** One bootstrap-grid row of the parts_by_others_list heading/data blocks
 *  (parts_by_others_list.tmpl:54-72 / :73-91 — the same 4/3/3/1/1 + 4/4/2/2
 *  column split). */
const PartsByOthersCols = ({
  left,
  right,
  headingRow,
}: {
  left: [string, string, string, string, string];
  right: [ReactNode, string, ReactNode, string];
  headingRow?: boolean;
}) => (
  <div className={headingRow ? 'list-heading row' : 'row list-rows'}>
    <div className="col-xs-4 padding-right-0">
      <div className="row">
        <div className="col-xs-4 padding-right-0">{left[0]}</div>
        <div className="col-xs-3 padding-right-0">{left[1]}</div>
        <div className="col-xs-3 padding-right-0">{left[2]}</div>
        <div className="col-xs-1 padding-right-0">{left[3]}</div>
        <div className="col-xs-1 padding-right-0 number-col">{left[4]}</div>
      </div>
    </div>
    <div className="col-xs-8 padding-left-0">
      <div className="row">
        <div className="col-xs-4 padding-right-0">{right[0]}</div>
        <div className="col-xs-4 padding-right-0">{right[1]}</div>
        <div className="col-xs-2 padding-right-0 number-col">{right[2]}</div>
        <div className="col-xs-2 padding-right-0">{right[3]}</div>
      </div>
    </div>
  </div>
);

/** parts_by_others_list.tmpl:1-112 (view parts_by_others_list.js:26-33) —
 *  spans header (group "Purchasing"), then the grid heading and TWO
 *  identical placeholder data rows (tmpl:73-91 = :92-110, including the
 *  truncated "155,99" weight). The old ul-based list above them
 *  (tmpl:8-53) is entirely inside an HTML comment — never rendered, not
 *  ported. */
export const PartsByOthersListTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <StubTabHeader group="Purchasing" name={report.name} nameSpace />
    <div className="content parts-by-others-content">
      <div className="content-item table-wrapper">
        <PartsByOthersCols
          headingRow
          left={['ITEM', 'AHU TAG', 'SECTION : TAG', 'UOM', 'QTY']}
          right={[
            'MANUFACTURER',
            'MPN',
            // tmpl:68 — "SHIP. WGT <span class="uom">(lb)</span>".
            <>
              SHIP. WGT <span className="uom">(lb)</span>
            </>,
            'SUPPLIED BY',
          ]}
        />
        {[0, 1].map((i) => (
          <PartsByOthersCols
            key={i}
            left={['VFDVFDVFDVFDVFDV', 'Eliminator:Elim1', 'Fan:Fan4', 'ea', '99']}
            right={[
              'MANUFACTURERMANUFACTURERgthyju',
              'MANUFACTURERMANUFACTURERgthyjuMANUFACTURERMANUFACTURERgthyju',
              '155,99',
              'Supplied by',
            ]}
          />
        ))}
      </div>
    </div>
  </div>
);
